import React, { useState } from 'react';
import api from '../lib/axios';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Send, Image as ImageIcon, Loader2 } from 'lucide-react';

export default function SendMessage() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  
  // Form State
  const [targetNumber, setTargetNumber] = useState('');
  const [messageText, setMessageText] = useState('');
  const [mediaFile, setMediaFile] = useState(null);

  const handleSendText = async (e) => {
    e.preventDefault();
    if (!targetNumber || !messageText) return;
    
    setLoading(true);
    try {
      // Hits the billing-protected text endpoint [cite: 379]
      const { data } = await api.post('/whatsapp/send-text', {
        number: targetNumber,
        message: messageText
      });

      if (data.success) {
        toast({ title: 'Message Sent!', description: `Successfully delivered to ${targetNumber}` });
        setMessageText('');
        setTargetNumber('');
      }
    } catch (error) {
      toast({
        title: 'Failed to Send',
        description: error.response?.data?.error || 'Ensure you have sufficient balance and your WhatsApp is connected.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendMedia = async (e) => {
    e.preventDefault();
    if (!targetNumber || !mediaFile) return;

    setLoading(true);
    try {
      // Must use FormData for file uploads [cite: 379]
      const formData = new FormData();
      formData.append('number', targetNumber);
      formData.append('file', mediaFile);
      if (messageText) {
        formData.append('caption', messageText);
      }

      // Hits the billing-protected media endpoint
      const { data } = await api.post('/whatsapp/send-media', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (data.success) {
        toast({ title: 'Media Sent!', description: `Successfully delivered to ${targetNumber}` });
        setMessageText('');
        setTargetNumber('');
        setMediaFile(null);
        // Reset file input UI manually
        document.getElementById('file-upload').value = '';
      }
    } catch (error) {
      toast({
        title: 'Failed to Send Media',
        description: error.response?.data?.error || 'Ensure your file is within limits and you have balance.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 max-w-3xl mx-auto space-y-8">
      <div>
        <h1 className="text-3xl font-bold mb-2">Quick Send</h1>
        <p className="text-muted-foreground">Instantly send a single message to a contact.</p>
      </div>

      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle>Compose Message</CardTitle>
          <CardDescription>
            Note: Standard messaging rates apply based on your tier.
          </CardDescription>
        </CardHeader>
        <CardContent>
          
          <Tabs defaultValue="text" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="text">Text Message</TabsTrigger>
              <TabsTrigger value="media">Image / Media</TabsTrigger>
            </TabsList>

            {/* TEXT MESSAGE TAB */}
            <TabsContent value="text">
              <form onSubmit={handleSendText} className="space-y-4">
                <div className="space-y-2">
                  <Label>WhatsApp Number (with Country Code)</Label>
                  <Input 
                    placeholder="e.g., 919876543210" 
                    value={targetNumber}
                    onChange={(e) => setTargetNumber(e.target.value)}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Message content</Label>
                  <Textarea 
                    placeholder="Hello! How can I help you today?" 
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    rows={5}
                    required
                  />
                </div>

                <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700">
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                  Send Text
                </Button>
              </form>
            </TabsContent>

            {/* MEDIA MESSAGE TAB */}
            <TabsContent value="media">
              <form onSubmit={handleSendMedia} className="space-y-4">
                <div className="space-y-2">
                  <Label>WhatsApp Number (with Country Code)</Label>
                  <Input 
                    placeholder="e.g., 919876543210" 
                    value={targetNumber}
                    onChange={(e) => setTargetNumber(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>Upload Image</Label>
                  <div className="flex items-center gap-4">
                    <Input 
                      id="file-upload"
                      type="file" 
                      accept="image/jpeg, image/png, image/webp"
                      onChange={(e) => setMediaFile(e.target.files[0])}
                      required
                      className="cursor-pointer"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Max size: 5MB. JPEG, PNG, WEBP allowed[cite: 35, 560].</p>
                </div>
                
                <div className="space-y-2">
                  <Label>Caption (Optional)</Label>
                  <Textarea 
                    placeholder="Check out this image!" 
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    rows={3}
                  />
                </div>

                <Button type="submit" disabled={loading || !mediaFile} className="w-full bg-blue-600 hover:bg-blue-700">
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ImageIcon className="mr-2 h-4 w-4" />}
                  Upload & Send Media
                </Button>
              </form>
            </TabsContent>
          </Tabs>

        </CardContent>
      </Card>
    </div>
  );
}